import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import {
  Card,
  Grid,
  Switch,
  Accordion,
  Container,
  IconButton,
  Typography,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  FormControlLabel,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import debounce from '../../../utils/debounce';
import Iconify from '../../../components/iconify';
import QuestionList from './Question/QuestionList';
import Tags from '../../../components/tags/tagsForm';
import { useFormContext } from '../../../hooks/useForm';
import TextInput from '../../../components/FormInputs/TextInput';
import SelectInput from '../../../components/FormInputs/SelectInput';
import { getUsersByDepartment } from '../../../services/user.service';
import CheckboxInput from '../../../components/FormInputs/CheckboxInput';
import DurationSelector from '../../../components/durationSelecor/DurationSelector';

FormSection.propTypes = {
  section: PropTypes.object,
  index: PropTypes.number,
  setFocusedSection: PropTypes.func,
  focusedSection: PropTypes.string,
  sectionLength: PropTypes.number,
  selectedDepartment: PropTypes.number,
  errorsTags: PropTypes.object,
  setErrorsTags: PropTypes.func,
};

const iconButtonStyle = {
  color: 'white',
  transition: 'color 0.3s',
  '&:hover': {
    color: (theme) => theme.palette.grey[500],
  },
};

export default function FormSection({
  section,
  index,
  setFocusedSection,
  focusedSection,
  sectionLength,
  selectedDepartment,
  errorsTags,
  setErrorsTags,
}) {
  const {
    deleteSection,
    addSection,
    addQuestion,
    modifySection,
    getOrderedSections,
    questions,
  } = useFormContext();

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [expanded, setExpanded] = useState(true);
  const [sectionList, setSectionList] = useState([]);
  const [agents, setAgents] = useState(section?.agents);

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const isBranchingActive = useMemo(() => {
    const questionIds = section?.questions || [];
    return questionIds.some((qId) => questions[qId]?.redirectOnAnswer === true);
  }, [section.questions, questions]);

  const filteredAgents = useMemo(
    () => agents?.map((agent) => ({ text: agent.username, value: agent.id })) || [],
    [agents]
  );

  const defaultfilteredAgents = useMemo(
    () => (filteredAgents ? filteredAgents?.map((f) => f.value) : []),
    [filteredAgents]
  );

  const handlePrioritySeceltion = (subPriority) => {
    modifySection(section.id, { ...section, priority: subPriority });
  };

  const handleCategoryChange = (cat) => {
    modifySection(section.id, { ...section, category: cat });
  };

  const handleDurationChange = (updatedDuration) => {
    modifySection(section.id, { ...section, duration: updatedDuration });
  };

  const handleSectionNameChange = (name) => {
    modifySection(section.id, { ...section, name });
  };

  const handleAutoAssignToggle = (e) => {
    modifySection(section.id, { ...section, autoAssign: e.target.checked });
  };

  useEffect(() => {
    if (expanded === true) setFocusedSection(section.id);
  }, [expanded, section.id, setFocusedSection]);

  useEffect(() => {
    setExpanded(focusedSection === section.id);
  }, [focusedSection, section.id]);

  useEffect(() => {
    if (selectedDepartment && section.nextSection === 'submit') {
      getUsersByDepartment(selectedDepartment).then((res) => {
        setAgents(res.filter((agent) => !agent.archived));
      });
    }
  }, [selectedDepartment, section.nextSection]);

  useEffect(() => {
    const sections = getOrderedSections()
      .filter((s) => s.id !== section.id)
      .map((sec) => ({
        text: `${t('pages.formPage.section')} ${sec.name}`,
        value: sec.id,
      }));
    sections.push({ text: t('buttons.submit'), value: 'submit' });
    setSectionList(sections);
  }, [getOrderedSections, t, section.id]);

  const handleNextSectionSelection = (nextSectionID) => {
    modifySection(section.id, { ...section, nextSection: nextSectionID });
  };

  const handleAddSection = () => {
    debounce(
      addSection({ name: sectionLength + 1, questions: [], isFirstSection: false }),
      500
    );
  };

  const handleAgentSelection = (selectedAgents) => {
    modifySection(section.id, { ...section, agents: selectedAgents });
  };

  return (
    <Grid container mt={2}>
      <Grid item xs={11}>
        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
          <AccordionSummary
            expandIcon={<Iconify icon="ooui:expand" width={isSmallScreen ? 14 : 20} />}
            sx={{
              backgroundColor: isDark ? 'background.neutral' : 'aliceblue',
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              borderBottomLeftRadius: expanded ? '0px' : '10px',
              borderBottomRightRadius: expanded ? '0px' : '10px',
            }}
          >
            <TextInput
              defaultValue={section.name || index}
              label={t('pages.formPage.sectionName')}
              onBlur={(e) => handleSectionNameChange(e.target.value)}
              sx={{ marginRight: '2vh' }}
            />
          </AccordionSummary>

          {expanded && (
            <AccordionDetails>
              <QuestionList id={section.id} departmentId={selectedDepartment} />

              {section.nextSection === 'submit' && (
                <>
                  <SelectInput
                    options={[
                      { text: t('formControl.selectPriority'), value: 'sel' },
                      { text: t('priority.HIGH'), value: 'HIGH' },
                      { text: t('priority.MEDIUM'), value: 'MEDIUM' },
                      { text: t('priority.LOW'), value: 'LOW' },
                    ]}
                    variant="standard"
                    sx={{ width: '100%', mt: 2, mb: 2 }}
                    setSelectedInput={handlePrioritySeceltion}
                    defaultValue={section.priority || 'sel'}
                    labelValue={t('attributes.priority')}
                  />

                  <FormControlLabel
                    sx={{ my: 1.5, width: '100%' }}
                    label="Auto-assign"
                    control={
                      <Switch
                        checked={section.autoAssign ?? false}
                        onChange={handleAutoAssignToggle}
                        size="small"
                      />
                    }
                  />

                  {filteredAgents && section.autoAssign ? (
                    <CheckboxInput
                      options={filteredAgents}
                      variant="standard"
                      sx={{ width: '100%', mt: 1 }}
                      defaultValue={section.agents || defaultfilteredAgents || []}
                      setSelectedVals={handleAgentSelection}
                      label={t('attributes.agents')}
                    />
                  ) : null}

                  <Box sx={{ mt: 3 }}>
                    <DurationSelector
                      days={parseInt(section.duration?.days || 0, 10)}
                      setDays={(v) =>
                        handleDurationChange({ ...section.duration, days: v })
                      }
                      hours={parseInt(section.duration?.hours || 1, 10)}
                      setHours={(v) =>
                        handleDurationChange({ ...section.duration, hours: v })
                      }
                      minutes={parseInt(section.duration?.minutes || 0, 10)}
                      setMinutes={(v) =>
                        handleDurationChange({ ...section.duration, minutes: v })
                      }
                    />
                  </Box>

                  <TextInput
                    defaultValue={section.category || ''}
                    fullWidth
                    label={t('attributes.category')}
                    sx={{ mt: 2 }}
                    onBlur={(e) => handleCategoryChange(e.target.value)}
                  />

                  <Tags
                    section={section}
                    selectedDepartment={selectedDepartment}
                    errorsTags={errorsTags}
                    setErrorsTags={setErrorsTags}
                  />
                </>
              )}
            </AccordionDetails>
          )}

          {/* PHASE 3 RESOLUTION: Hide static footer if dynamic branching is active */}
          {!isBranchingActive && (
            <AccordionActions
              sx={{
                backgroundColor: isDark ? 'background.neutral' : 'aliceblue',
                borderBottomLeftRadius: '10px',
                borderBottomRightRadius: '10px',
                p: 2,
              }}
            >
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item xs={2}>
                  <IconButton onClick={() => deleteSection(section.id)} color="error">
                    <Iconify icon="eva:trash-2-fill" />
                  </IconButton>
                </Grid>
                <Grid item xs={9} container alignItems="center" spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="body2" noWrap>
                      {t('pages.formPage.afterSection')} <b>{section.name}</b> :
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <SelectInput
                      options={sectionList}
                      variant="standard"
                      sx={{ width: '100%' }}
                      setSelectedInput={handleNextSectionSelection}
                      defaultValue={section.nextSection || ''}
                      labelValue={t('pages.formPage.continueTo')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </AccordionActions>
          )}
        </Accordion>
      </Grid>

      {expanded && focusedSection === section.id && (
        <Grid item xs={1}>
          <Container
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              position: 'relative',
            }}
          >
            <Card
              raised
              sx={{
                top: '50vh',
                opacity: '0.9',
                position: 'sticky',
                display: 'inline-grid',
                bgcolor: '#2065D1',
                p: 0.2,
              }}
            >
              <IconButton onClick={() => addQuestion(section.id, {})}>
                <Iconify icon="simple-line-icons:plus" sx={iconButtonStyle} width={20} />
              </IconButton>
              <IconButton onClick={handleAddSection}>
                <Iconify icon="bi:sign-intersection" sx={iconButtonStyle} width={20} />
              </IconButton>
              <IconButton onClick={() => deleteSection(section.id)}>
                <Iconify icon="eva:trash-2-fill" sx={iconButtonStyle} width={20} />
              </IconButton>
            </Card>
          </Container>
        </Grid>
      )}
    </Grid>
  );
}
